<template>
  <div class="inverse-schedules-panel">
    <div v-if="!hideTitle" class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">
        <a-icon type="swap" style="color: #fa8c16" class="mr-1" />
        {{ $t("encaissementValidation.repaymentSchedules") }}
      </h5>
    </div>

    <a-alert
      v-if="repaymentComplete"
      type="success"
      show-icon
      class="mb-3"
      :message="$t('encaissementValidation.repaymentComplete')"
    />

    <!-- Received installments (read-only) -->
    <template v-if="receivedSchedules.length">
      <div class="text-muted small font-weight-bold mb-2">{{ $t("encaissementValidation.statusReceived") }}</div>
      <a-table
        row-key="_id"
        :columns="receivedColumns"
        :data-source="receivedSchedules"
        :pagination="false"
        size="small"
        class="mb-4"
      >
        <template slot="dueDate" slot-scope="text">
          {{ formatDate(text) }}
        </template>
        <template slot="amount" slot-scope="text">
          {{ text != null ? text.toFixed(3) : "—" }}
        </template>
      </a-table>
    </template>

    <!-- Pending: batch edit then Save -->
    <div v-if="!repaymentComplete" class="pending-draft-section">
      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
        <span class="font-weight-bold">{{ $t("encaissementValidation.pendingInverseSchedules") }}</span>
        <a-button type="dashed" size="small" icon="plus" @click="addDraftRow">
          {{ $t("fournisseur.addSchedule") }}
        </a-button>
      </div>

      <div v-if="draftRows.length === 0 && !loadingSchedules" class="text-muted small mb-3">
        {{ $t("saisiePaiement.batchSchedulesEmptyHint") }}
      </div>

      <div
        v-for="(row, index) in draftRows"
        :key="row.key"
        :class="['draft-row mb-2 d-flex flex-wrap align-items-center', { 'draft-row-highlight': isHighlightDraftRow(row) }]"
      >
        <a-date-picker
          v-model="row.dueDate"
          format="DD/MM/YYYY"
          class="draft-row-date"
          :placeholder="$t('fournisseur.dueDate')"
          @change="() => onDraftDateChange(index)"
        />
        <a-input-number
          v-model="row.amount"
          :min="0.001"
          :step="0.001"
          class="draft-row-amount ml-2"
          :placeholder="$t('fournisseur.scheduledAmount')"
          @change="touchDraft"
        />
        <a-button type="link" class="text-danger ml-1" @click="removeDraftRow(row.key)">
          <a-icon type="delete" />
        </a-button>
      </div>

      <div
        class="sum-match-bar mt-3 mb-3"
        :class="{ 'sum-match-bar--ok': sumMatches, 'sum-match-bar--bad': !sumMatches && draftRows.length > 0 }"
      >
        <span>
          {{ $t("saisiePaiement.batchPendingTotal") }}: <strong>{{ draftSum.toFixed(3) }}</strong>
          — {{ $t("saisiePaiement.batchRequiredRemaining") }}: <strong>{{ remainingToSchedule.toFixed(3) }}</strong>
        </span>
        <a-icon v-if="sumMatches && draftRows.length" type="check-circle" class="ml-2 text-success" />
        <a-icon v-else-if="draftRows.length && !sumMatches" type="exclamation-circle" class="ml-2 text-warning" />
      </div>

      <div class="mb-2 d-flex flex-wrap align-items-center" style="gap: 8px">
        <a-button type="primary" :loading="savingBatch" :disabled="!canSaveBatch" @click="saveBatch">
          {{ $t("action.enregistrer") }}
        </a-button>
        <a-button :disabled="!draftDirty" @click="resetDraft">{{ $t("action.annuler") }}</a-button>
        <span v-if="draftDirty" class="text-muted small">{{ $t("saisiePaiement.unsavedSchedulesHint") }}</span>
      </div>
    </div>

    <div v-if="schedules.length === 0 && !loadingSchedules" class="text-center text-muted mt-3">
      {{ $t("fournisseur.noSchedules") }}
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { fetchInverseSchedules, replacePendingInverseSchedules } from "@/services/api/compV3/encaissement";
import { showApiError } from "@/utilities/showApiError";

function newDraftKey() {
  return `inv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default {
  name: "InverseSchedulesPanel",
  props: {
    depense: { type: Object, default: null },
    highlightScheduleId: { type: [String, Object], default: null },
    /** When embedded in a drawer that already has a section title */
    hideTitle: { type: Boolean, default: false },
  },
  data() {
    return {
      schedules: [],
      loadingSchedules: false,
      draftRows: [],
      draftBaseline: "",
      savingBatch: false,
    };
  },
  computed: {
    depenseTotal() {
      if (this.depense && this.depense._totalAmount != null) {
        return Number(this.depense._totalAmount);
      }
      if (!this.depense) return 0;
      return Number(this.depense.amountTTC || this.depense.amount || this.depense.money || 0);
    },
    repaymentComplete() {
      return !!(this.depense && this.depense.repaymentStatus === "repaid");
    },
    receivedSchedules() {
      return (this.schedules || [])
        .filter((s) => s.status === "received")
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    },
    receivedSum() {
      return this.receivedSchedules.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    },
    remainingToSchedule() {
      const r = this.depenseTotal - this.receivedSum;
      return Number(r.toFixed(3));
    },
    draftSum() {
      return this.draftRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    },
    sumMatches() {
      if (this.remainingToSchedule <= 0.0005) {
        return this.draftRows.length === 0 || Math.abs(this.draftSum) < 0.0005;
      }
      return Math.abs(this.draftSum - this.remainingToSchedule) < 0.009;
    },
    draftDirty() {
      return this.serializeDraft() !== this.draftBaseline;
    },
    canSaveBatch() {
      if (!this.depense || this.repaymentComplete || !this.draftDirty) return false;
      if (!this.sumMatches) return false;
      if (this.remainingToSchedule > 0.0005 && this.draftRows.length === 0) return false;
      for (const r of this.draftRows) {
        if (!r.dueDate || r.amount == null || Number(r.amount) <= 0) return false;
      }
      return true;
    },
    receivedColumns() {
      return [
        { title: this.$t("fournisseur.dueDate"), dataIndex: "dueDate", key: "due", scopedSlots: { customRender: "dueDate" } },
        { title: this.$t("fournisseur.scheduledAmount"), dataIndex: "amount", key: "amt", scopedSlots: { customRender: "amount" } },
      ];
    },
  },
  watch: {
    depense: {
      immediate: true,
      handler(d) {
        if (d && d._id) {
          this.loadSchedules();
        } else {
          this.schedules = [];
          this.draftRows = [];
          this.draftBaseline = "";
        }
      },
    },
  },
  methods: {
    isHighlightDraftRow(row) {
      if (!this.highlightScheduleId || !row.scheduleId) return false;
      return String(row.scheduleId) === String(this.highlightScheduleId);
    },
    serializeDraft() {
      return JSON.stringify(
        this.draftRows.map((r) => ({
          due: r.dueDate && r.dueDate.toISOString ? r.dueDate.toISOString() : r.dueDate,
          amt: r.amount,
        }))
      );
    },
    touchDraft() {
      this.$forceUpdate();
    },
    recalcMonthlyDraftDatesFromFirst() {
      if (!this.draftRows.length) return;
      const first = this.draftRows[0].dueDate;
      if (!first) return;
      const firstM = moment(first);
      if (!firstM.isValid()) return;
      for (let i = 1; i < this.draftRows.length; i++) {
        this.draftRows[i].dueDate = moment(firstM).add(i, "months");
      }
    },
    onDraftDateChange(index) {
      if (index === 0) {
        this.recalcMonthlyDraftDatesFromFirst();
      }
      this.touchDraft();
    },
    formatDate(val) {
      return val ? moment(val).format("DD/MM/YYYY") : "—";
    },
    buildDraftFromSchedules() {
      const pending = (this.schedules || []).filter((s) => s.status === "pending");
      return pending.map((s) => ({
        key: `s-${s._id}`,
        scheduleId: s._id,
        dueDate: moment(s.dueDate),
        amount: Number(s.amount),
      }));
    },
    setBaselineFromDraft() {
      this.draftBaseline = this.serializeDraft();
    },
    async loadSchedules() {
      if (!this.depense || !this.depense._id) return;
      this.loadingSchedules = true;
      try {
        const res = await fetchInverseSchedules(this.depense._id);
        this.schedules = res.data || [];
        this.draftRows = this.buildDraftFromSchedules();
        this.setBaselineFromDraft();
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.loadingSchedules = false;
      }
    },
    addDraftRow() {
      if (this.repaymentComplete) return;
      const newIndex = this.draftRows.length;
      let dueDate = null;
      if (this.draftRows.length && this.draftRows[0].dueDate) {
        const firstM = moment(this.draftRows[0].dueDate);
        if (firstM.isValid()) {
          dueDate = moment(firstM).add(newIndex, "months");
        }
      }
      this.draftRows.push({
        key: newDraftKey(),
        scheduleId: null,
        dueDate,
        amount: null,
      });
    },
    removeDraftRow(key) {
      this.draftRows = this.draftRows.filter((r) => r.key !== key);
      this.recalcMonthlyDraftDatesFromFirst();
    },
    resetDraft() {
      this.draftRows = this.buildDraftFromSchedules();
      this.setBaselineFromDraft();
    },
    async saveBatch() {
      if (!this.canSaveBatch) return;
      this.savingBatch = true;
      try {
        const schedules = this.draftRows.map((r) => {
          const due = r.dueDate && r.dueDate.toDate ? r.dueDate.toDate() : r.dueDate;
          return { dueDate: due, amount: Number(r.amount) };
        });
        await replacePendingInverseSchedules(this.depense._id, schedules);
        this.$message.success(this.$t("saisiePaiement.batchSchedulesSaved"));
        await this.loadSchedules();
        this.$emit("refresh");
      } catch (e) {
        showApiError(this, e);
      } finally {
        this.savingBatch = false;
      }
    },
  },
};
</script>

<style scoped>
.draft-row {
  gap: 8px;
}
.draft-row-date {
  min-width: 150px;
}
.draft-row-amount {
  width: 140px;
  max-width: 100%;
}
.draft-row-highlight {
  border-left: 3px solid #fa8c16;
  padding-left: 10px;
  margin-left: -4px;
}
.sum-match-bar {
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.sum-match-bar--ok {
  border-color: #b7eb8f;
  background: #f6ffed;
}
.sum-match-bar--bad {
  border-color: #ffe58f;
  background: #fffbe6;
}
.schedule-add-form {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 12px;
}
</style>
